import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { meApi } from "../../api/me";
import type { TokenOut } from "../../api/types";
import { errorMessage } from "../../shared/errors";
import { formatDateTime } from "../../shared/format";
import { Alert, Button, Card, Input } from "../../ui";

export function TokensPage() {
  const queryClient = useQueryClient();
  const tokens = useQuery({
    queryKey: ["me", "tokens"],
    queryFn: meApi.tokens,
  });
  const clients = useQuery({
    queryKey: ["me", "clients"],
    queryFn: meApi.clients,
  });
  const [form, setForm] = useState({
    product: "tradebo",
    label: "",
    scopes: "tradebo.tools.read",
  });
  const [created, setCreated] = useState<TokenOut | null>(null);
  const [clientForm, setClientForm] = useState({ name: "", redirect_uris: "" });
  const [error, setError] = useState<string | null>(null);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["me"] });

  const create = useMutation({
    mutationFn: () =>
      meApi.createToken({
        product: form.product,
        label: form.label,
        scopes: form.scopes.split(/[\s,]+/).filter(Boolean),
      }),
    onSuccess: (token) => {
      setCreated(token);
      setForm({ ...form, label: "" });
      setError(null);
      return refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const revoke = useMutation({
    mutationFn: (id: string) => meApi.revokeToken(id),
    onSuccess: refresh,
  });
  const createClient = useMutation({
    mutationFn: () =>
      meApi.createClient({
        name: clientForm.name,
        redirect_uris: clientForm.redirect_uris.split(/[\s,]+/).filter(Boolean),
        product: form.product,
      }),
    onSuccess: () => {
      setClientForm({ name: "", redirect_uris: "" });
      setError(null);
      return refresh();
    },
    onError: (err) => setError(errorMessage(err)),
  });
  const disableClient = useMutation({
    mutationFn: (id: string) => meApi.disableClient(id),
    onSuccess: refresh,
  });

  return (
    <div className="flex flex-col gap-6">
      <Card>
        <h1 className="mb-4 text-xl font-semibold">API tokens</h1>
        {error ? <Alert kind="error">{error}</Alert> : null}
        {created?.token ? (
          <Alert kind="success">
            <div>Copy your new token now; it will not be shown again.</div>
            <code className="mt-1 block break-all rounded bg-white p-2 text-xs">
              {created.token}
            </code>
          </Alert>
        ) : null}
        <form
          className="my-4 grid max-w-2xl gap-3 md:grid-cols-3"
          aria-label="Create token"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <Input
            label="Product"
            required
            value={form.product}
            onChange={(e) => setForm({ ...form, product: e.target.value })}
          />
          <Input
            label="Label"
            required
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
          />
          <Input
            label="Scopes"
            required
            value={form.scopes}
            onChange={(e) => setForm({ ...form, scopes: e.target.value })}
          />
          <div className="md:col-span-3">
            <Button type="submit" loading={create.isPending}>
              Create token
            </Button>
          </div>
        </form>
        <ul className="divide-y divide-slate-100 text-sm">
          {(tokens.data ?? []).map((t) => (
            <li key={t.id} className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium">
                  {t.label}{" "}
                  <code className="ml-2 text-xs text-slate-500">
                    {t.prefix}…
                  </code>
                </div>
                <div className="text-xs text-slate-500">
                  {t.product} · {t.scopes.join(" ")} · last used{" "}
                  {formatDateTime(t.last_used_at)}
                </div>
              </div>
              <Button variant="danger" onClick={() => revoke.mutate(t.id)}>
                Revoke
              </Button>
            </li>
          ))}
          {tokens.data?.length === 0 ? (
            <li className="py-2 text-slate-500">No tokens yet.</li>
          ) : null}
        </ul>
      </Card>
      <Card>
        <h2 className="mb-4 text-xl font-semibold">MCP clients</h2>
        <form
          className="my-4 grid max-w-2xl gap-3 md:grid-cols-2"
          aria-label="Create MCP client"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            createClient.mutate();
          }}
        >
          <Input
            label="Name"
            required
            value={clientForm.name}
            onChange={(e) =>
              setClientForm({ ...clientForm, name: e.target.value })
            }
          />
          <Input
            label="Redirect URIs"
            required
            placeholder="http://127.0.0.1:6274/oauth/callback"
            value={clientForm.redirect_uris}
            onChange={(e) =>
              setClientForm({ ...clientForm, redirect_uris: e.target.value })
            }
          />
          <div className="md:col-span-2">
            <Button type="submit" loading={createClient.isPending}>
              Register client
            </Button>
          </div>
        </form>
        <ul className="divide-y divide-slate-100 text-sm">
          {(clients.data ?? []).map((c) => (
            <li key={c.id} className="flex items-center justify-between py-2">
              <div>
                <div className="font-medium">{c.name}</div>
                <div className="text-xs text-slate-500">
                  client_id <code>{c.client_id}</code> ·{" "}
                  {c.redirect_uris.join(", ")}
                </div>
              </div>
              <Button
                variant="danger"
                onClick={() => disableClient.mutate(c.id)}
              >
                Remove
              </Button>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}
